import { ApiHttpError, ApiUnauthorizedError } from './errors.js';

export interface ArafFactoryClientConfig {
  baseUrl: string;
  adminToken?: string;
  bearerToken?: string;
}

let _config: ArafFactoryClientConfig = { baseUrl: '' };

export function configureArafFactory(config: ArafFactoryClientConfig): void {
  _config = { ..._config, ...config };
}

export function updateArafBearerToken(token: string): void {
  _config = { ..._config, bearerToken: token };
}

/**
 * Custom orval fetch mutator.
 *
 * Returns { data, status, headers } matching orval's tagged-union response types.
 * Throws ApiUnauthorizedError on 401, ApiHttpError on other non-2xx.
 * React Query catches thrown errors; success responses are unwrapped via unwrapData().
 */
export async function arafFetch<T>(url: string, options: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (_config.adminToken) {
    headers['x-admin-token'] = _config.adminToken;
  }
  if (_config.bearerToken) {
    headers.Authorization = `Bearer ${_config.bearerToken}`;
  }

  const fullUrl = `${_config.baseUrl}${url}`;

  const method = (options.method ?? 'GET').toUpperCase();

  let response: Response;
  try {
    response = await fetch(fullUrl, { ...options, headers });
  } catch (err) {
    throw new ApiHttpError(
      0,
      err instanceof Error ? err.message : 'Network error',
      'NETWORK_ERROR',
      undefined,
      url,
      method,
    );
  }

  const { status } = response;
  const responseHeaders = response.headers;
  const traceId = response.headers.get('X-Request-Id') ?? undefined;

  if (status === 401) throw new ApiUnauthorizedError();

  if (!response.ok) {
    let message: string | undefined;
    let code: string | undefined;
    try {
      const body = (await response.json()) as {
        message?: string;
        code?: string;
        detail?: string;
        title?: string;
      };
      message =
        (typeof body.detail === 'string' ? body.detail : undefined) ??
        (typeof body.message === 'string' ? body.message : undefined) ??
        (typeof body.title === 'string' ? body.title : undefined);
      code = typeof body.code === 'string' ? body.code : undefined;
    } catch {
      // ignore parse errors
    }
    throw new ApiHttpError(status, message, code, traceId, url, method);
  }

  const data = status === 204 ? undefined : await response.json();
  return { data, status, headers: responseHeaders } as T;
}

/**
 * Extracts .data from an orval response object.
 * Since errors are thrown by arafFetch, .data is always the success payload.
 */
export function unwrapData<TData>(response: { data: TData } | null | undefined): TData | null {
  if (response == null) return null;
  return response.data ?? null;
}
